// Libraries
import { CommonModule } from '@angular/common';
import {
  AfterViewInit, ChangeDetectorRef, Component, computed, effect, inject, OnDestroy, ViewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import {
  DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE, MatDateFormats, MatNativeDateModule, NativeDateAdapter,
} from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatPaginator, MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatSelectModule } from '@angular/material/select';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatToolbarModule } from '@angular/material/toolbar';
import { ActivatedRoute, convertToParamMap, NavigationEnd, NavigationStart, Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { debounceTime, filter, takeUntil } from 'rxjs/operators';

// Services
import { Pop } from 'src/app/classes/shared/pop';
import { ProjectService } from 'src/app/services/api/projects/project.service';
import { UserService } from 'src/app/services/api/users/user.service';
import { InterventionStore } from 'src/app/services/stores/intervention.store';

// Interfaces
import { Intervention, InterventionEvaluationResult } from 'src/app/interfaces/api/intervention';
import { defaultProject, Project } from 'src/app/interfaces/api/project';
import { User } from 'src/app/interfaces/api/user';

// Classes
import { TimeFormatPipe } from 'src/app/pipes/time-format.pipe';

// Components
import { NoDataComponent } from 'projects/ui-library/src/lib/components/no-data/no-data.component';
import { ButtonComponent, ChipComponent, SearchBarComponent, SpinnerComponent } from 'ui-library';
import { InterventionDrawerComponent } from './dialogs/intervention-drawer/intervention-drawer.component';

// Displays and parses dates as JJ/MM/AAAA, matching the format used everywhere else in the app
// (see the identical override in intervention-drawer.component.ts).
const INTERVENTION_DATE_FORMATS: MatDateFormats = {
  parse: {
    dateInput: null,
  },
  display: {
    dateInput: { day: '2-digit', month: '2-digit', year: 'numeric' },
    monthYearLabel: { year: 'numeric', month: 'short' },
    dateA11yLabel: { year: 'numeric', month: 'long', day: 'numeric' },
    monthYearA11yLabel: { year: 'numeric', month: 'long' },
  },
};

@Component({
  selector: 'app-interventions',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatToolbarModule,
    MatButtonModule,
    MatCardModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatTableModule,
    MatSortModule,
    MatPaginatorModule,
    MatIconModule,
    ChipComponent,
    TranslateModule,
    TimeFormatPipe,
    ButtonComponent,
    SearchBarComponent,
    SpinnerComponent,
    NoDataComponent,
    InterventionDrawerComponent,
  ],
  providers: [
    { provide: MAT_DATE_LOCALE, useValue: 'fr-FR' },
    { provide: DateAdapter, useClass: NativeDateAdapter },
    { provide: MAT_DATE_FORMATS, useValue: INTERVENTION_DATE_FORMATS },
  ],
  templateUrl: './interventions.component.html',
  styleUrls: ['./interventions.component.scss'],
})

export class InterventionsComponent implements AfterViewInit, OnDestroy {
  private readonly interventionStore: InterventionStore = inject(InterventionStore);
  private readonly projectService: ProjectService = inject(ProjectService);
  private readonly userService: UserService = inject(UserService);
  private readonly route: ActivatedRoute = inject(ActivatedRoute);
  private readonly router: Router = inject(Router);
  private readonly translate: TranslateService = inject(TranslateService);
  private readonly pop: Pop = inject(Pop);
  private readonly cdr: ChangeDetectorRef = inject(ChangeDetectorRef);

  @ViewChild('interventionPaginator', { static: false }) interventionPaginator: MatPaginator = new MatPaginator();
  @ViewChild(MatSort) sort: MatSort = new MatSort();


  readonly adminMode: boolean = this.route.snapshot.data?.['adminMode'] === true;
  private readonly basePath: string = this.adminMode ? '/admin/all-interventions' : '/project/interventions';
  private isActive: boolean = this.router.url?.startsWith(this.basePath) ?? false;

  project: Project = defaultProject;
  usersById: Record<string, User> = {};
  users: User[] = [];
  destroy$ = new Subject<void>();
  searchText: string = '';

  drawerOpen: boolean = false;
  // See closeDrawerSilently(): true only for the one forced flush that snaps the drawer straight
  // to 'closed' before this view gets detached by the router, so no transition plays across it.
  drawerAnimationDisabled: boolean = false;
  // See remountDrawer(): briefly toggled false→true on every reattachment to force a fresh DOM
  // element (and a fresh <app-intervention-drawer> instance) for the *ngIf-wrapped drawer, clearing
  // the animation engine's stale bookkeeping for the previous (reused-by-detach) element.
  drawerMounted: boolean = true;
  drawerMode: 'add' | 'edit' = 'add';
  selectedIntervention: Intervention | null = null;
  // Set via the `?tracker=` deep-link param when the drawer is opened from a Dispositif's
  // "Maintenance" panel (see ResourcesComponent) or from the Devices tab's edit dialog (see
  // EditTrackerModal) - either way, the new intervention gets linked to that device without the
  // user having to pick it manually. Only ever set for an EXISTING device (real uuid) - both
  // callers only offer this once the device has actually been saved.
  presetTrackerId: string | null = null;
  // Which page to redirect back to once the drawer closes/saves - set via the `?returnTo=`
  // deep-link param alongside `?tracker=` above, since ResourcesComponent and DevicesComponent
  // each need their own distinct return path (see returnToCallerIfNeeded()).
  returnTarget: 'resources' | 'devices' | null = null;
  filtersOpen: boolean = false;
  // True only while resolving a deep-link (?edit=id) with no cached row to show yet.
  loadingDetail: boolean = false;

  interventionsData: MatTableDataSource<Intervention> = new MatTableDataSource();
  interventionsColumns: string[] = ['dueDate', 'category', 'subType', 'description', 'assignee', 'evaluationResult'];
  // Cap applied to the table/card preview only — the full description is still shown in the
  // edit drawer.
  private readonly DESCRIPTION_PREVIEW_WORD_LIMIT = 300;
  visibleRows: Intervention[] = [];
  private dataSourceChange$ = new Subject<void>();
  private lastFocusedElement: HTMLElement | null = null;
  // app-search-bar emits on every keystroke (no built-in debounce) — debounced here before it
  // reaches the store, since each filter change now fires a real server request (I-09).
  private searchSubject$ = new Subject<string>();

  evaluationResultOptions: { value: InterventionEvaluationResult | 'NON_EVALUEE' | ''; label: string }[] = [
    { value: '', label: 'form.all' },
    { value: 'EFFICACE', label: 'interventions.evaluation.EFFICACE' },
    { value: 'PARTIELLEMENT_EFFICACE', label: 'interventions.evaluation.PARTIELLEMENT_EFFICACE' },
    { value: 'INEFFICACE', label: 'interventions.evaluation.INEFFICACE' },
    { value: 'NON_EVALUEE', label: 'interventions.evaluation.NON_EVALUEE' },
  ];

  // Signals
  readonly loading = this.interventionStore.loading;
  readonly error = this.interventionStore.error;
  readonly referential = this.interventionStore.referential;
  readonly filters = this.interventionStore.filters;
  readonly results = this.interventionStore.results;
  readonly count = this.interventionStore.count;

  readonly categories = computed(() => Object.keys(this.referential()));

  readonly subTypeFilterOptions = computed(() => {
    const category = this.filters().category;
    return (category && this.referential()[category]) || [];
  });

  constructor() {
    if (this.adminMode) {
      this.interventionsColumns = ['project', ...this.interventionsColumns];
      this.interventionStore.loadReferential();
      this.interventionStore.loadAllInterventions();
    } else {
      this.projectService.currentProject.pipe(takeUntil(this.destroy$)).subscribe(project => {
        const changed = this.project && this.project.id !== project.id;
        this.project = project;

        if (changed && project?.id && this.isActive) {
          this.loadUsers();
          this.interventionStore.loadReferential();
          this.interventionStore.loadInterventions(project.id);
        }
      });
    }

    this.searchSubject$.pipe(debounceTime(300), takeUntil(this.destroy$)).subscribe(text => {
      this.interventionStore.setFilters({ search: text });
    });

    effect(() => {
      // `results` is already the current server-paginated/filtered page — the paginator is NOT
      // attached to this dataSource, since paging is driven by the server (see onPageChange).
      const rows = this.results();
      this.dataSourceChange$.next();
      this.interventionsData = new MatTableDataSource(rows);
      this.interventionsData.sortingDataAccessor = (row, columnId) => this.sortValueFor(row, columnId);
      this.interventionsData.sort = this.sort;

      // Mirrors the table's (client-)sorted current page for the mobile card list.
      this.interventionsData.connect().pipe(takeUntil(this.dataSourceChange$), takeUntil(this.destroy$))
        .subscribe(visibleRows => (this.visibleRows = visibleRows));
    });
  }

  private sortValueFor(row: Intervention, columnId: string): string | number {
    switch (columnId) {
      case 'dueDate': return row.dueDate ? new Date(row.dueDate).getTime() : 0;
      case 'evaluationResult': return row.evaluation?.result || '';
      case 'assignee': return this.userLabel(row.assignee);
      default: return (row as any)[columnId] ?? '';
    }
  }

  // Table/card preview only — cuts to the first N words instead of a raw character length so it
  // doesn't sever a word mid-way.
  descriptionPreview(description?: string): string {
    if (!description) return '—';

    const words = description.trim().split(/\s+/);
    if (words.length <= this.DESCRIPTION_PREVIEW_WORD_LIMIT) return description;

    return words.slice(0, this.DESCRIPTION_PREVIEW_WORD_LIMIT).join(' ') + '…';
  }

  ngAfterViewInit(): void {
    this.sort.sortChange.pipe(takeUntil(this.destroy$)).subscribe(sort => {
      this.interventionStore.setSort(sort.active, sort.direction);
    });

    this.processDeepLinkParams(this.router.url);
    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      takeUntil(this.destroy$),
    ).subscribe(event => {
      this.remountDrawer();
      this.processDeepLinkParams(event.urlAfterRedirects);

      this.isActive = event.urlAfterRedirects.startsWith(this.basePath);

      if (this.isActive) {
        if (this.adminMode)
          this.interventionStore.loadAllInterventions();
        else if (this.project?.id)
          this.interventionStore.loadInterventions(this.project.id);
      }
    });

    this.router.events.pipe(
      filter((event): event is NavigationStart => event instanceof NavigationStart),
      takeUntil(this.destroy$),
    ).subscribe(event => {
      if (this.drawerOpen && !event.url.startsWith('/project/interventions')) {
        this.closeDrawerSilently();
      }
    });
  }

  private closeDrawerSilently(): void {
    this.drawerAnimationDisabled = true;
    this.drawerOpen = false;
    this.selectedIntervention = null;
    this.presetTrackerId = null;
    this.returnTarget = null;
    this.interventionStore.select(null);
    this.cdr.detectChanges();

    Promise.resolve().then(() => { this.drawerAnimationDisabled = false; });
  }

  private remountDrawer(): void {
    this.drawerMounted = false;
    this.cdr.detectChanges();
    this.drawerMounted = true;
    this.cdr.detectChanges();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.dataSourceChange$.complete();
  }

  private processDeepLinkParams(url: string): void {
    if (!url.startsWith('/project/interventions')) return;

    Promise.resolve().then(() => {
      const queryParamMap = convertToParamMap(this.router.parseUrl(url).queryParams);
      const openAdd = queryParamMap.get('add') === 'true';
      const editId = queryParamMap.get('edit');
      const trackerId = queryParamMap.get('tracker');
      const returnTo = queryParamMap.get('returnTo') === 'devices' ? 'devices' : 'resources';

      if (openAdd) {
        this.openAdd(trackerId || undefined, trackerId ? returnTo : null);
      } else if (editId) {
        this.openById(editId);
      } else if (this.drawerOpen) {
        this.drawerOpen = false;
        this.selectedIntervention = null;
        this.presetTrackerId = null;
        this.returnTarget = null;
        this.interventionStore.select(null);
      }
    });
  }

  private loadUsers(): void {
    if (!this.project?.id) return;

    this.userService.getUsersLight(this.project.id).pipe(takeUntil(this.destroy$)).subscribe({
      next: (users: User[]) => {
        this.users = users || [];
        this.usersById = {};
        this.users.forEach(u => { if (u?.id) this.usersById[u.id] = u; });
      },
      error: () => { this.users = []; this.usersById = {}; },
    });
  }

  userLabel(id?: string): string {
    if (!id) return '—';

    const user = this.usersById[id];
    if (!user) return this.translate.instant('interventions.inactive_account');

    const name = [user.firstName, user.lastName].filter(Boolean).join(' ').trim() || user.email || '—';
    return user.active === false
      ? `${name} (${this.translate.instant('interventions.inactive_account_suffix')})`
      : name;
  }

  assigneeLabel(row: Intervention): string {
    if (!row.assigneeName) return this.translate.instant('interventions.inactive_account');

    return row.assigneeActive === false
      ? `${row.assigneeName} (${this.translate.instant('interventions.inactive_account_suffix')})`
      : row.assigneeName;
  }

  private openById(id: string): void {
    this.loadingDetail = true;

    this.interventionStore.fetchIntervention(id).pipe(takeUntil(this.destroy$)).subscribe({
      next: (intervention) => {
        this.loadingDetail = false;
        this.openEdit(intervention);
      },
      error: (err) => {
        this.loadingDetail = false;
        console.error('[Interventions] Failed to load intervention from query param:', err);
      },
    });
  }

  onPageChange(event: PageEvent): void {
    this.interventionStore.setPage(event.pageIndex, event.pageSize);
  }

  search(text: string = ''): void {
    this.searchText = text;
    this.searchSubject$.next(text);
  }

  setCategoryFilter(value: string): void {
    this.interventionStore.setFilters({ category: value, subType: '' });
  }

  setSubTypeFilter(value: string): void {
    this.interventionStore.setFilters({ subType: value });
  }

  setApplicantFilter(value: string): void {
    this.interventionStore.setFilters({ applicant: value });
  }

  setAssigneeFilter(value: string): void {
    this.interventionStore.setFilters({ assignee: value });
  }

  setDateFromFilter(value: string): void {
    this.interventionStore.setFilters({ dateFrom: value });
  }

  setDateToFilter(value: string): void {
    this.interventionStore.setFilters({ dateTo: value });
  }

  setEvaluationResultFilter(value: InterventionEvaluationResult | 'NON_EVALUEE' | ''): void {
    this.interventionStore.setFilters({ evaluationResult: value });
  }

  resetFilters(): void {
    this.searchText = '';
    this.interventionStore.resetFilters();
  }

  toggleFilters(): void {
    this.filtersOpen = !this.filtersOpen;
  }

  evaluationLabelKey(result?: string): string {
    return 'interventions.evaluation.' + (result || 'NON_EVALUEE');
  }

  retry(): void {
    if (this.adminMode) this.interventionStore.loadAllInterventions();
    else if (this.project?.id) this.interventionStore.loadInterventions(this.project.id);
  }

  onRowKeydown(event: KeyboardEvent, intervention: Intervention): void {
    if (event.key !== 'Enter' && event.key !== ' ') return;

    event.preventDefault();
    this.openEdit(intervention);
  }

  openAdd(trackerId?: string, returnTarget: 'resources' | 'devices' | null = null): void {
    if (this.adminMode) return;

    this.lastFocusedElement = document.activeElement as HTMLElement;
    this.drawerMode = 'add';
    this.selectedIntervention = null;
    this.presetTrackerId = trackerId ?? null;
    this.returnTarget = this.presetTrackerId ? returnTarget : null;
    this.interventionStore.select(null);
    this.drawerOpen = true;
  }

  openEdit(intervention: Intervention): void {
    // Switching to another intervention while a tracker-linked creation is in progress would
    // silently carry `presetTrackerId` over to this unrelated edit, wrongly triggering the
    // "return to caller" redirect (see returnToCallerIfNeeded) once THIS edit is saved or
    // cancelled — even though it has nothing to do with the abandoned linked creation.
    if (this.drawerOpen && this.drawerMode === 'add' && this.presetTrackerId) {
      this.pop.confirm(this.translate.instant('interventions.confirm_abandon_linked_creation'), (confirm: any) => {
        if (confirm?.ok) {
          this.presetTrackerId = null;
          this.returnTarget = null;
          this.doOpenEdit(intervention);
        }
      });
      return;
    }

    this.doOpenEdit(intervention);
  }

  private doOpenEdit(intervention: Intervention): void {
    this.lastFocusedElement = document.activeElement as HTMLElement;
    this.drawerMode = 'edit';
    this.selectedIntervention = intervention;
    this.interventionStore.select(intervention.id);
    this.drawerOpen = true;

    this.interventionStore.fetchIntervention(intervention.id).pipe(takeUntil(this.destroy$)).subscribe({
      next: (fresh) => {
        if (this.drawerOpen && this.selectedIntervention?.id === fresh.id) this.selectedIntervention = fresh;
      },
      error: (err) => console.error('[Interventions] Failed to refresh intervention detail:', err),
    });
  }

  private restoreFocus(): void {
    this.lastFocusedElement?.focus();
    this.lastFocusedElement = null;
  }

  onDrawerClosed(): void {
    this.drawerOpen = false;
    this.interventionStore.select(null);
    this.restoreFocus();
    this.returnToCallerIfNeeded(false);
  }

  onDrawerSaved(_intervention: Intervention): void {
    this.drawerOpen = false;
    this.interventionStore.select(null);
    this.restoreFocus();
    this.returnToCallerIfNeeded(true);
  }

  // Opened from a Dispositif's "Maintenance" panel (ResourcesComponent) or the Devices tab's edit
  // dialog (EditTrackerModal) via the `?tracker=`/`?returnTo=` deep-link — whether the user saved
  // or cancelled, they land back on that same device (Ressources' side panel, or the Devices
  // list) rather than staying on the Interventions list they never meant to browse.
  private returnToCallerIfNeeded(linked: boolean): void {
    if (!this.presetTrackerId) return;

    const trackerId = this.presetTrackerId;
    const target = this.returnTarget ?? 'resources';
    this.presetTrackerId = null;
    this.returnTarget = null;

    if (target === 'devices') {
      this.router.navigate(['/devices'], { queryParams: { tracker: trackerId } });
      return;
    }

    const queryParams: Record<string, string | number> = { tab: 1, tracker: trackerId };
    if (linked) queryParams['linked'] = 'success';

    this.router.navigate(['/project/project-settings/resources'], { queryParams });
  }

  onDrawerDeleted(_id: string): void {
    this.drawerOpen = false;
    this.interventionStore.select(null);
    this.restoreFocus();
  }

  evaluationChipColor(result?: string): 'primary' | 'warning' | 'danger' | 'muted' | '' {
    switch (result) {
      case 'EFFICACE': return '';
      case 'PARTIELLEMENT_EFFICACE': return '';
      case 'INEFFICACE': return 'danger';
      default: return 'muted';
    }
  }

  evaluationChipHexColor(result?: string): string {
    switch (result) {
      case 'EFFICACE': return '#4d6700';
      case 'PARTIELLEMENT_EFFICACE': return '#b25400';
      default: return '';
    }
  }
}
